"use client"

// React imports
import React from "react"

// Next.js imports
import dynamic from "next/dynamic"

// External SDK imports
import { BetType, useBets, type UseBetsProps } from "@azuro-org/sdk"
import { OrderDirection } from "@azuro-org/toolkit"
import { Message } from "@locmod/intl"
import { useClient } from "wagmi"

// Type imports
import type { Address } from "viem"
import type { SupportedNetwork } from "@/Overtime/types/network"

// Internal hooks and utilities
import { useWallet } from "wallet"
import useChainProviderHook from "@/hooks/useChainProvider"
import { useUserTicketsQuery } from "@/Overtime/queries/useUserTicketsQuery"
import { mapOvertimeTicketToAzuroBet } from "src/helpers/mapOvertimeTicketToAzuroBet"
import { useUserSxBets } from "@/hooks/useUserSxBets"
import { mapSxBetTradeToBet } from "@/helpers/mapSxBetTradeToBet"

// Components
import { Button } from "components/inputs"
import EmptyContentComp from "compositions/EmptyContent/EmptyContent"

// Local imports
import { BetCard } from "./components"
import messages from "./messages"

const BetDetailsModal = dynamic(
  () => import("./components/BetDetailsModal/BetDetailsModal")
)

// Loading skeleton component
const LoadingSkeleton = () => (
  <div className="bone rounded-sm h-[7.75rem] w-full" />
)

// Empty state component
const EmptyContent = () => (
  <EmptyContentComp
    className="py-6"
    image="/images/illustrations/smile_sad.png"
    title={messages.empty.title}
    text={messages.empty.text}
  />
)

// Bet list section component
const BetListSection = ({
  bets,
  isOvertimeChain,
  isSXChain,
}: {
  bets: any[]
  isOvertimeChain: boolean
  isSXChain: boolean
}) => (
  <>
    <div className="p-3">
      <Message
        className="text-caption-14 font-semibold"
        value={{
          ...messages.title,
          values: { count: bets.length },
        }}
      />
    </div>
    <div className="space-y-2 max-h-[28rem] overflow-auto no-scrollbar">
      {bets.map(bet => (
        <BetCard
          key={bet.tokenId}
          bet={bet}
          source={isOvertimeChain ? "overtime" : isSXChain ? "sxbet" : "azuro"}
        />
      ))}
    </div>
    <Button
      className="w-full mt-3"
      to="/bets"
      style="tertiary"
      title={messages.allBets}
      size={40}
    />
    <BetDetailsModal />
  </>
)

// Overtime-specific logic hook
const useOvertimeBets = (
  account: string | undefined,
  isOvertimeChain: boolean,
  chainId: number
) => {
  const client = useClient()

  const { data: overtimeTickets, isLoading } = useUserTicketsQuery(
    account || "",
    { networkId: chainId as SupportedNetwork, client },
    {
      enabled: !!account && isOvertimeChain,
    }
  )

  const acceptedBets = React.useMemo(() => {
    if (!overtimeTickets) {
      return []
    }

    return overtimeTickets
      .map((ticket: any) => mapOvertimeTicketToAzuroBet(ticket))
      .filter((bet: any) => bet.status === "Accepted")
  }, [overtimeTickets])

  return { acceptedBets, isLoading }
}

// SX-specific logic hook
const useSXBets = (
  account: string | undefined,
  isSXChain: boolean,
  chainId?: number
) => {
  const { data: sxBetTradesQuery, isLoading } = useUserSxBets(
    isSXChain ? account : undefined,
    isSXChain ? chainId : undefined
  )

  const acceptedBets = React.useMemo(() => {
    if (!sxBetTradesQuery?.pages) {
      return []
    }

    const allSxBetTrades = sxBetTradesQuery.pages.flatMap(page => page.trades)
    const mappedSxBets = allSxBetTrades.map(trade => mapSxBetTradeToBet(trade))

    return mappedSxBets.filter((bet: any) => bet.status === "Accepted")
  }, [sxBetTradesQuery])

  return { acceptedBets, isLoading }
}

// Azuro-specific logic hook
const useAzuroBets = (account: string | undefined, isAzuroChain: boolean) => {
  const queryProps = React.useMemo(
    () => ({
      filter: {
        bettor: account! as Address,
        type: BetType.Accepted,
      },
      itemsPerPage: 500,
      orderDir: OrderDirection.Desc,
      query: {
        enabled: isAzuroChain,
      },
    }),
    [account]
  )

  const { data, isLoading, isFetching } = useBets(queryProps)

  const acceptedBets = React.useMemo(() => {
    const { pages } = data || {}
    if (!pages?.length) {
      return []
    }

    return pages.flatMap(({ bets }) => bets)
  }, [data])

  return { acceptedBets, isLoading: isLoading || isFetching }
}

// Main component
const AcceptedBets: React.FC = () => {
  const { account } = useWallet()

  const { isOvertimeChain, isAzuroChain, isSXChain, chainId } =
    useChainProviderHook()

  // Get bets based on chain
  const overtimeBets = useOvertimeBets(account, isOvertimeChain, chainId)
  const sxBets = useSXBets(account, isSXChain, chainId)
  const azuroBets = useAzuroBets(account, isAzuroChain)

  // Determine which bets to use based on chain
  let acceptedBets: any[] = []
  let isLoading = false

  if (isOvertimeChain) {
    acceptedBets = overtimeBets.acceptedBets
    isLoading = overtimeBets.isLoading
  } else if (isSXChain) {
    acceptedBets = sxBets.acceptedBets
    isLoading = sxBets.isLoading
  } else {
    acceptedBets = azuroBets.acceptedBets
    isLoading = azuroBets.isLoading
  }

  // Show loading state
  if (isLoading) {
    return <LoadingSkeleton />
  }

  // Show empty state
  if (!acceptedBets.length) {
    return <EmptyContent />
  }

  // Show bet list
  return (
    <BetListSection
      bets={acceptedBets}
      isOvertimeChain={isOvertimeChain}
      isSXChain={isSXChain}
    />
  )
}

export default AcceptedBets
